/**
 * AISlot Component
 * Visual indicator for AI-generated track suggestions
 * Similar to SmartSlot but with blue styling and AI-specific functionality
 */

import { useState } from 'react';
import { Bot, RefreshCw, Eye, ChevronDown, X, Check, Loader2, Play, Pause } from 'lucide-react';
import type { AISlot as AISlotType } from '../../stores/workbench';
import { Modal } from '../shared/Modal';
import { useTrackPreview } from '@/hooks';

interface AISlotProps {
  slot: AISlotType;
  onInsertTrack: (trackId: string) => void;
  onRegenerate: () => void;
  onRemove: () => void;
}

export function AISlot({ slot, onInsertTrack, onRegenerate, onRemove }: AISlotProps) {
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const { playPreview, stopPreview, previewTrackId, isPlaying, isPending } = useTrackPreview({
    onError: (err) => console.error('Preview playback failed:', err),
  });

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleTrackClick = (trackId: string) => {
    stopPreview();
    onInsertTrack(trackId);
  };

  return (
    <>
      <div
        className={`
          mx-4 my-2 p-3 rounded-lg border-2 border-dashed
          transition-all
          ${
            slot.filled
              ? 'border-zinc-700 bg-zinc-800/50'
              : 'border-blue-500 bg-blue-500/10 hover:border-blue-400 hover:bg-blue-500/15'
          }
        `}
        data-slot-id={slot.id}
      >
        {/* Header */}
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div
            className={`
              p-2 rounded-lg
              ${slot.filled ? 'bg-zinc-800' : 'bg-blue-500/20'}
            `}
          >
            {slot.filled ? (
              <Check className="w-4 h-4 text-blue-400" />
            ) : isRegenerating ? (
              <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
            ) : (
              <Bot className="w-4 h-4 text-blue-500" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-zinc-300">
              {slot.filled ? 'AI Track Inserted' : `AI Suggestions`}
            </div>
            {!slot.filled && (
              <div className="text-xs text-zinc-500">
                {slot.suggestedTracks.length === 0 ? (
                  isRegenerating ? 'Generating suggestions...' : 'No suggestions available'
                ) : (
                  `${slot.suggestedTracks.length} ${slot.suggestedTracks.length === 1 ? 'track' : 'tracks'} suggested`
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* View Prompt */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPromptModal(true);
              }}
              className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 transition-colors"
              title="View prompt"
            >
              <Eye className="w-4 h-4" />
            </button>

            {/* Regenerate */}
            {!slot.filled && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRegenerate();
                }}
                disabled={isRegenerating}
                className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Regenerate suggestions"
              >
                <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
              </button>
            )}

            {/* Toggle Suggestions */}
            {!slot.filled && slot.suggestedTracks.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSuggestions(!showSuggestions);
                }}
                className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 transition-colors"
                title={showSuggestions ? 'Hide suggestions' : 'Show suggestions'}
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`}
                />
              </button>
            )}

            {/* Close Slot */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-600 hover:text-zinc-400 transition-colors"
              title="Close AI slot"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Suggestions List */}
        {!slot.filled && showSuggestions && slot.suggestedTracks.length > 0 && (
          <div className="mt-3 pt-3 border-t border-blue-500/20 space-y-1">
            <div className="text-xs font-medium text-blue-400 mb-2">
              AI Suggestions
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {slot.suggestedTracks.map((track, index) => (
                <div
                  key={track.id}
                  className="w-full flex items-center gap-3 p-2 bg-zinc-800/50 rounded"
                >
                  {/* Album Art + Track Info (clickable to insert) */}
                  <button
                    onClick={() => handleTrackClick(track.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 hover:bg-zinc-700/50 transition-colors p-1 rounded text-left"
                    title={`${track.name} by ${track.artists.map(a => a.name).join(', ')}`}
                  >
                    {/* Album Art */}
                    {track.album.images && track.album.images.length > 0 && (
                      <img
                        src={track.album.images[track.album.images.length - 1]?.url}
                        alt={track.album.name}
                        className="w-10 h-10 rounded flex-shrink-0"
                      />
                    )}

                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-200 truncate">
                        {track.name}
                      </div>
                      <div className="text-xs text-zinc-400 truncate">
                        {track.artists.map(a => a.name).join(', ')}
                      </div>
                    </div>

                    {/* Position */}
                    <div className="text-xs text-blue-400 flex-shrink-0">
                      #{index + 1}
                    </div>
                  </button>

                  {/* Preview button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (previewTrackId === track.id && isPlaying) {
                        stopPreview();
                      } else {
                        playPreview({
                          id: track.id,
                          uri: track.uri,
                          durationMs: track.durationMs,
                        });
                      }
                    }}
                    disabled={isPending}
                    className="p-2 rounded hover:bg-blue-500/20 transition-colors flex-shrink-0 disabled:opacity-50"
                    title={previewTrackId === track.id && isPlaying ? 'Stop preview' : 'Preview track'}
                  >
                    {previewTrackId === track.id && isPlaying ? (
                      <Pause className="w-4 h-4 text-blue-400" />
                    ) : (
                      <Play className="w-4 h-4 text-blue-400" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Prompt Modal */}
      <PromptModal
        open={showPromptModal}
        prompt={slot.promptUsed}
        onClose={() => setShowPromptModal(false)}
      />
    </>
  );
}

/**
 * PromptModal Component
 * Displays the AI prompt used to generate track suggestions
 */
interface PromptModalProps {
  open: boolean;
  prompt: string;
  onClose: () => void;
}

function PromptModal({ open, prompt, onClose }: PromptModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="AI Prompt Used"
      className="max-w-2xl w-full mx-4"
    >
      <div className="px-6 py-4">
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4 overflow-auto max-h-96">
          <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono">
            {prompt}
          </pre>
        </div>
        <div className="mt-4 text-xs text-zinc-500">
          This prompt was sent to the AI to generate track suggestions for this slot.
        </div>
      </div>
    </Modal>
  );
}
